
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import ClientsListItem from '@/modules/GatewayModule/components/ClientsListItem/index.vue'
import ClientsActions from '@/modules/GatewayModule/components/ClientsActions/index.vue'
import ClientsFilters from '@/modules/GatewayModule/components/ClientsFilters/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    ClientsListParamsT,
    CreateClientPayloadT,
    GatewayClientT,
} from '@/core/types/gateway.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// composable
import useActions from '@/common/composable/useActions'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'clients-list-item': ClientsListItem,
        'clients-actions': ClientsActions,
        'clients-filters': ClientsFilters,
    },
    setup() {
        const state = reactive({
            clients: new DataState<any>({}),
        })

        const filter_state: ClientsListParamsT = reactive({
            name: undefined as string | undefined,
        })

        const loadClients = async (page: number) => {
            state.clients.pagination.current_page = page

            try {
                state.clients.is_loading = true

                const response = await gateway.getClients(page, filter_state)
                state.clients.list = response.data
                state.clients.pagination.total_pages = response.meta.last_page

                state.clients.is_failed = false
                state.clients.failure.error_state.is_cancel = false
            } catch (error) {
                state.clients.failure.catchError(error)

                if (!state.clients.failure.error_state.is_cancel) {
                    state.clients.is_failed = true
                }
            } finally {
                if (!state.clients.failure.error_state.is_cancel) {
                    state.clients.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadClients(1)
        })

        const { actions_state, startCreate, startUpdate, startDelete } =
            useActions<GatewayClientT>()

        const createClient = async (client: CreateClientPayloadT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.createClient(client)
                await loadClients(state.clients.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateClient = async (client: CreateClientPayloadT & { id: number }) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.updateClient(client.id, client)
                await loadClients(state.clients.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteClient = async (client_id: number) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.deleteClient(client_id)
                await loadClients(state.clients.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSearch = (client: ClientsListParamsT) => {
            filter_state.name = client.name

            loadClients(1)
        }

        const onReset = () => {
            filter_state.name = undefined

            loadClients(1)
        }

        return {
            state,
            loadClients,
            actions_state,
            startCreate,
            startUpdate,
            startDelete,
            createClient,
            updateClient,
            deleteClient,
            // filters
            onSearch,
            onReset,
        }
    },
})
